<template>
  <Modal
    :show="show"
    hide-default-actions
    :width="modalWidth"
    @close="handleClose"
  >
    <template #title>
      <h5>{{ results ? resultsTitle : title }}</h5>
    </template>
    <template #content>
      <template v-if="!results">
        <p class="instruction-text text-body-3 mb-5">
          {{ instructionText }}
        </p>
        <p
          v-if="tooManySelected"
          class="validation-message text-body-3"
        >
          Please select 50 or fewer employees.
        </p>
        <div class="employee-table">
          <div class="table-header">
            <span class="table-header-cell text-body-2">Employee</span>
            <span class="table-header-cell text-body-2">Job Profile</span>
          </div>
          <div class="table-body text-body-3">
            <div
              v-for="employee in employees"
              :key="employee.ulid"
              class="table-row"
            >
              <span class="table-cell">{{ employeeDisplayName(employee) }}</span>
              <span class="table-cell">{{ employee.job_profile_title || '—' }}</span>
            </div>
          </div>
        </div>
        <div class="action-section">
          <DpplNewButton
            class="w-100"
            color="primary"
            size="small"
            :disabled="loading || !employees.length || tooManySelected"
            @click="handleConfirm"
          >
            {{ loading ? loadingText : buttonText }}
          </DpplNewButton>
        </div>
      </template>
      <template v-else>
        <div class="employee-table">
          <div class="table-header">
            <span class="table-header-cell text-body-2">Employee</span>
            <span class="table-header-cell text-body-2">E-mail Results</span>
          </div>
          <div class="table-body text-body-3">
            <div
              v-for="result in resultsWithEmployees"
              :key="result.employee_ulid"
              class="table-row"
            >
              <span class="table-cell">{{ employeeDisplayName(result.employee) }}</span>
              <span
                class="table-cell result-status"
                :class="result.isSuccess ? 'success' : 'error'"
              >
                {{ getStatusLabel(result.status) }}
              </span>
            </div>
          </div>
        </div>
        <div class="action-section">
          <DpplNewButton
            class="w-100"
            color="primary"
            size="small"
            @click="handleClose"
          >
            Close
          </DpplNewButton>
        </div>
      </template>
    </template>
  </Modal>
</template>

<script setup>
import { computed } from 'vue';
import Modal from '@/components/shared/Modal.vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  employees: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  results: { type: Array, default: null },
  title: { type: String, required: true },
  resultsTitle: { type: String, required: true },
  instructionText: { type: String, required: true },
  buttonText: { type: String, required: true },
  loadingText: { type: String, required: true },
  statusLabels: { type: Object, required: true },
  successStatuses: { type: Array, default: () => ['success'] },
});

const emit = defineEmits(['close', 'confirm']);

const modalWidth = computed(() => (props.results ? '460px' : '376px'));

const tooManySelected = computed(() => (props.employees?.length || 0) > 50);

const resultsWithEmployees = computed(() => props.results?.map((result) => ({
  ...result,
  employee: props.employees.find((e) => e.ulid === result.employee_ulid),
  isSuccess: props.successStatuses.includes(result.status),
})) || []);

const employeeDisplayName = (employee) => {
  if (!employee) return '—';

  const parts = [employee.first_name, employee.last_name].filter(Boolean);

  return parts.length ? parts.join(' ') : employee.email || '—';
};

const getStatusLabel = (status) => props.statusLabels[status] || status;

const handleClose = () => {
  emit('close');
};

const handleConfirm = () => {
  emit('confirm');
};
</script>

<style scoped lang="less">
:deep(.content) {
  margin-top: 0 !important;
}

.instruction-text {
  color: @text-light;
}

.validation-message {
  color: @text-light;
  margin: 0 0 16px;
}

.employee-table {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 16px;
  margin-bottom: 4px;
}

.table-header-cell {
  color: @text-darkest;
  font-weight: 600;
}

.table-body {
  max-height: 240px;
  overflow-y: auto;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 16px;
}

.table-cell {
  color: @text-light;
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-status {
  &.success {
    color: @teal-500;
  }

  &.error {
    color: @red-500;
  }
}

.action-section {
  border-top: 1px solid @stroke-default;
  padding-top: 16px;
  margin-top: 16px;
}

:deep(.modal-content-wrapper.pr-6.pl-6) {
  padding: 0 !important;
}
</style>
